import { useEffect, useState } from "react";
import { Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime, fmtEuro } from "@/lib/format";
import { Btn, Field, Modal, Pill } from "@/components/rp";
import { Section } from "@/components/commesse";
import { InvoiceDialog, invoiceText } from "@/components/admin/invoice";

const TONE = { bozza: "muted", inviato: "warning", accettato: "success", rifiutato: "error" };

const NewExtra = ({ open, onClose, oid, onSaved }) => {
  const [f, setF] = useState({ title: "", amount: "", description: "" });
  const [file, setFile] = useState(null);
  useEffect(() => { if (open) { setF({ title: "", amount: "", description: "" }); setFile(null); } }, [open]);
  const save = async () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { onSaved((await api.post(`/amministrazione/${oid}/extras`, fd)).data); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title="Nuovo extra" description="Il cliente vedrà il documento di spesa e potrà accettarlo firmando sul suo dispositivo." testid="extra-dialog">
      <div className="space-y-3">
        <Field label="Descrizione breve"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rp-field" placeholder="Es. Spostamento punto acqua cucina" data-testid="extra-title" /></Field>
        <Field label="Importo (€)"><input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="rp-field" data-testid="extra-amount" /></Field>
        <Field label="Dettagli (facoltativo)"><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rp-field" data-testid="extra-description" /></Field>
        <Field label="Documento di spesa (PDF o foto)"><input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="extra-file" /></Field>
      </div>
      <Btn className="mt-4" disabled={f.title.trim().length < 3 || !(Number(f.amount) > 0)} onClick={save} data-testid="extra-save">Salva extra</Btn>
    </Modal>
  );
};

const InvoiceBox = ({ x, oid, setP }) => {
  const [open, setOpen] = useState(false);
  const send = async (done, v = {}) => {
    const fd = new FormData();
    fd.append("done", done);
    if (v.file) fd.append("file", v.file);
    if (v.invoice_number) fd.append("invoice_number", v.invoice_number);
    if (v.invoice_date) fd.append("invoice_date", v.invoice_date);
    try { setP((await api.post(`/amministrazione/${oid}/extras/${x.id}/invoice`, fd)).data); toast.success(done ? "Extra segnato come fatturato" : "Fatturazione annullata"); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="ml-auto flex flex-wrap items-center gap-2" data-testid={`extra-invoice-${x.id}`}>
      {x.invoiced_at && invoiceText(x) && <span className="text-xs font-medium" data-testid={`extra-invoice-info-${x.id}`}>{invoiceText(x)}</span>}
      {x.invoice_doc && <a href={fileUrl(`/api/amministrazione/${oid}/extras/${x.id}/file?which=invoice`)} target="_blank" rel="noreferrer" className="text-xs underline" data-testid={`extra-invoice-doc-${x.id}`}>{x.invoice_doc.filename}</a>}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!x.invoiced_at} onChange={(e) => (e.target.checked ? setOpen(true) : send(false))} className="h-4 w-4 accent-[#198754]" data-testid={`extra-invoice-check-${x.id}`} />
        {x.invoiced_at ? `Segnato il ${fmtDateTime(x.invoiced_at)} da ${x.invoiced_by}` : "Fatturato"}
      </label>
      <InvoiceDialog open={open} onClose={() => setOpen(false)} onConfirm={(v) => send(true, v)} withFile title={`Extra n. ${x.number} · ${x.title} · ${fmtEuro(x.amount)}`} />
    </span>
  );
};

export const ExtrasSection = ({ p, oid, setP }) => {
  const [open, setOpen] = useState(false);
  const act = async (fn, msg) => { try { setP((await fn()).data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const extras = p.extras || [];
  const accepted = extras.filter((e) => e.status === "accettato").reduce((s, e) => s + e.amount, 0);
  return (
    <Section label={`Extra · accettati ${fmtEuro(accepted)}`} testid="section-extras" action={<Btn variant="outline" className="h-9" onClick={() => setOpen(true)} data-testid="add-extra"><Plus className="h-4 w-4" /> Nuovo extra</Btn>}>
      {!extras.length && <p className="text-sm text-[#757575]">Nessun extra. Crea un extra con il documento di spesa e invialo al cliente: lo accetta firmando dall'app.</p>}
      <div className="space-y-2">
        {extras.map((x) => (
          <div key={x.id} className="rounded-2xl border border-[#EBEBEB] bg-white p-4" data-testid={`extra-${x.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-sm flex-1 min-w-[180px]">Extra n. {x.number} · {x.title}</span>
              <span className="text-sm font-semibold">{fmtEuro(x.amount)}</span>
              <Pill tone={TONE[x.status]}>{x.status_label}</Pill>
            </div>
            {x.description && <p className="text-sm text-[#757575] mt-1">{x.description}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
              {x.doc && <a href={fileUrl(`/api/amministrazione/${oid}/extras/${x.id}/file?which=doc`)} target="_blank" rel="noreferrer" className="underline" data-testid={`extra-doc-${x.id}`}>{x.doc.filename}</a>}
              {x.signed_doc && <a href={fileUrl(`/api/amministrazione/${oid}/extras/${x.id}/file?which=signed`)} target="_blank" rel="noreferrer" className="underline text-[#198754]" data-testid={`extra-signed-${x.id}`}>Accettazione firmata da {x.accepted_by} ({fmtDateTime(x.accepted_at)})</a>}
              {x.rejection_note && <span className="text-[#DC3545]">Motivo rifiuto: {x.rejection_note}</span>}
              {x.status === "accettato" && <InvoiceBox x={x} oid={oid} setP={setP} />}
              {x.status !== "accettato" && (
                <span className="ml-auto flex gap-2">
                  <Btn className="h-9" disabled={!x.doc} onClick={() => act(() => api.post(`/amministrazione/${oid}/extras/${x.id}/send`), "Extra inviato al cliente")} data-testid={`extra-send-${x.id}`}><Send className="h-4 w-4" /> {x.status === "bozza" ? "Invia al cliente" : "Invia di nuovo"}</Btn>
                  <button onClick={() => window.confirm("Eliminare l'extra?") && act(() => api.delete(`/amministrazione/${oid}/extras/${x.id}`))} className="text-[#757575] hover:text-[#DC3545]" data-testid={`extra-delete-${x.id}`}><Trash2 className="h-4 w-4" /></button>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <NewExtra open={open} onClose={() => setOpen(false)} oid={oid} onSaved={setP} />
    </Section>
  );
};

export const AdminClientChecklist = ({ oid }) => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/amministrazione/${oid}`).then((r) => setP(r.data)).catch(() => {}); }, [oid]);
  if (!p) return null;
  const check = async (it, done) => { try { setP((await api.post(`/amministrazione/${oid}/items/cliente/${it.key}/check`, { done })).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-6 pt-5 border-t border-[#EBEBEB]" data-testid="progress-client-checklist">
      <div className="flex justify-between text-xs mb-3"><span className="rp-label">Pratica cliente</span><span className="font-semibold">{p.client_progress.done} di {p.client_progress.total}</span></div>
      <div className="space-y-1.5">
        {p.client_items.map((it) => (
          <label key={it.key} className={`flex items-center gap-2 text-sm cursor-pointer ${it.status === "non_serve" ? "opacity-40" : ""}`} data-testid={`progress-item-${it.key}`}>
            <input type="checkbox" checked={it.status === "completata"} disabled={it.status === "non_serve"} onChange={(e) => check(it, e.target.checked)} className="h-4 w-4 accent-[#198754]" data-testid={`progress-check-${it.key}`} />
            <span className={it.status === "completata" ? "line-through text-[#757575]" : ""}>{it.label}</span>
            {it.status === "inviata" && <Pill tone="warning">In attesa del cliente</Pill>}
          </label>
        ))}
      </div>
    </div>
  );
};
